/**
 * "Colour the world": turns a painted world map (public/worlds/<id>.webp) into a pencil sketch on
 * warm paper. World maps use sketches built with the same recipe (scripts/world-sketches.tsx, no work on the device);
 * other images are computed on the device (canvas, works offline). Classic pencil-sketch recipe:
 * greyscale → colour-dodge with a blurred negative (= graphite outlines) → a soft tone wash →
 * graphite / paper tint → paper grain. Computed ones are object URLs in a 2-entry LRU (revoked on eviction).
 */
import { useEffect, useState } from 'react';

const cache = new Map<string, Promise<string | null>>();
const done = new Map<string, string | null>();

/** Separable box blur (two passes ≈ gaussian) of a single-channel image, in place. */
function boxBlur(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(src.length);
  const out = new Float32Array(src.length);
  const k = 1 / (2 * r + 1);
  for (let y = 0; y < h; y++) {
    const o = y * w;
    let acc = 0;
    for (let x = -r; x <= r; x++) acc += src[o + Math.min(w - 1, Math.max(0, x))]!;
    for (let x = 0; x < w; x++) {
      tmp[o + x] = acc * k;
      acc += src[o + Math.min(w - 1, x + r + 1)]! - src[o + Math.max(0, x - r)]!;
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let y = -r; y <= r; y++) acc += tmp[Math.min(h - 1, Math.max(0, y)) * w + x]!;
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc * k;
      acc += tmp[Math.min(h - 1, y + r + 1) * w + x]! - tmp[Math.max(0, y - r) * w + x]!;
    }
  }
  return out;
}

/** Deterministic hash noise in 0..1. */
const noise = (x: number, y: number) => {
  let n = (x * 374761393 + y * 668265263) | 0;
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
};

const PAPER = [251, 244, 229] as const;
const GRAPHITE = [86, 70, 62] as const;

/** The pencil-sketch recipe on raw RGBA pixels, in place (shared with scripts/world-sketches.tsx). */
export function sketchPixels(px: Uint8ClampedArray | Uint8Array, w: number, h: number): void {
  const n = w * h;
  const lum = new Float32Array(n);
  const inv = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const l = 0.3 * px[i * 4]! + 0.59 * px[i * 4 + 1]! + 0.11 * px[i * 4 + 2]!;
    lum[i] = l;
    inv[i] = 255 - l;
  }
  const r = Math.max(2, Math.round(w / 420));
  const blur = boxBlur(boxBlur(inv, w, h, r), w, h, r);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const l = lum[i]!;
      // Colour dodge: flat areas → white, edges → graphite lines.
      const dodge = Math.min(255, (l * 255) / Math.max(1, 255 - blur[i]!)) / 255;
      // Lines a touch darker, plus a light tone wash so shapes still read.
      const line = Math.pow(dodge, 2.4);
      const tone = 0.8 + 0.2 * (l / 255);
      const grain = 1 - 0.06 * noise(x, y) - 0.03 * noise(x >> 2, y >> 2);
      const v = Math.max(0, Math.min(1, line * tone * grain));
      const o = i * 4;
      px[o] = GRAPHITE[0] + (PAPER[0] - GRAPHITE[0]) * v;
      px[o + 1] = GRAPHITE[1] + (PAPER[1] - GRAPHITE[1]) * v;
      px[o + 2] = GRAPHITE[2] + (PAPER[2] - GRAPHITE[2]) * v;
      px[o + 3] = 255;
    }
}

/** Width of the pencil sketches (built ones and the runtime fallback): a soft graphite layer needs no more. */
export const SKETCH_W = 960;

export function sketchify(img: HTMLImageElement, maxW = SKETCH_W): HTMLCanvasElement | null {
  const w = Math.min(maxW, img.naturalWidth);
  const h = Math.round((img.naturalHeight / img.naturalWidth) * w);
  if (!w || !h) return null;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  if (!g) return null;
  g.drawImage(img, 0, 0, w, h);
  const id = g.getImageData(0, 0, w, h);
  sketchPixels(id.data, w, h);
  g.putImageData(id, 0, 0);
  return c;
}

const idle = (fn: () => void) => {
  const ric = (
    window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    }
  ).requestIdleCallback;
  if (ric) ric(fn, { timeout: 600 });
  else setTimeout(fn, 60);
};

/** Built sketch of a painted world map (scripts/world-sketches.tsx → /worlds/<id>-sketch.webp). */
export const builtSketchUrl = (url: string) => {
  const m = /^\/worlds\/([a-zA-Z]+)(?:-\d+)?\.webp$/.exec(url);
  return m ? `/worlds/${m[1]}-sketch.webp` : null;
};

/** Runtime sketches kept (object URLs, revoked when evicted): the current world and the one before. */
const MAX_RUNTIME = 2;

/** URL of the pencil-sketch version of `url`: the built file for world maps, else a computed object URL. */
export function sketchOf(url: string): Promise<string | null> {
  const hit = cache.get(url);
  if (hit) return hit;
  const built = builtSketchUrl(url);
  if (built) {
    const p = Promise.resolve(built);
    cache.set(url, p);
    done.set(url, built);
    return p;
  }
  // LRU over computed sketches: revoke the oldest object URL.
  const computed = [...done.entries()].filter(([, u]) => u?.startsWith('blob:'));
  while (computed.length >= MAX_RUNTIME) {
    const [k, u] = computed.shift()!;
    if (u) URL.revokeObjectURL(u);
    done.delete(k);
    cache.delete(k);
  }
  const p = new Promise<string | null>((resolve) => {
    if (typeof document === 'undefined') return resolve(null);
    const img = new Image();
    img.decoding = 'async';
    img.onload = () =>
      idle(() => {
        try {
          const c = sketchify(img);
          if (!c) return resolve(null);
          c.toBlob((b) => resolve(b ? URL.createObjectURL(b) : null), 'image/webp', 0.86);
        } catch {
          resolve(null);
        }
      });
    img.onerror = () => resolve(null);
    img.src = url;
  }).then((u) => {
    done.set(url, u);
    return u;
  });
  cache.set(url, p);
  return p;
}

/** Pencil-sketch object URL for a painted map (null until ready). */
export function useSketch(url: string | null): string | null {
  const [s, setS] = useState<string | null>(() => (url ? (done.get(url) ?? null) : null));
  useEffect(() => {
    if (!url) {
      setS(null);
      return;
    }
    let live = true;
    const known = done.get(url);
    if (known !== undefined) setS(known);
    else void sketchOf(url).then((u) => live && setS(u));
    return () => {
      live = false;
    };
  }, [url]);
  return s;
}
